import React, { useContext, useEffect, useMemo, useState, useCallback } from "react";
import {
  Drawer,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  InputBase,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Checkbox,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import BuildIcon from "@mui/icons-material/Build";
import { useDispatch, useSelector } from "react-redux";
import { getCspmResources, clearAllCspmMessages } from "../../../store/cspm/cspmSlice";
import SearchIcon from "@mui/icons-material/Search";
import ErrorUI from './ErrorUI';
import RemediationSnackbarProvider from '../../RemediationHub/components/common/SnackbarHost';
import RequestFixForm from '../../RemediationHub/components/RequestFixForm';
import { fetchActiveRequests } from '../../../store/remediation/remediationSlice';
import ActiveRemediationHint, {
  ActiveRemediationDetailPanel,
  RemediationStatusLegend,
} from './ActiveRemediationHint';
import { CloudProviderContext } from "../../../CloudProviderContext";
import RemediationPreviewPanel, { isHttpUrlString } from "../../../components/Common/RemediationPreviewPanel";

function getSeverityBadgeProps(sev) {
  const s = (sev || '').toUpperCase();
  let text = '#333';
  if (s === 'CRITICAL') text = '#c62828';
  else if (s === 'HIGH') text = '#ef6c00';
  else if (s === 'MEDIUM') text = '#fbc02d';
  else if (s === 'LOW') text = '#388e3c';
  else if (s === 'INFORMATIONAL') text = '#1976d2';
  return { text };
}

const CSPMResourcesDrawer = ({
  open,
  onClose,
  checkId,
  runId,
  availableRegions = [],
  availableTypes = [],
  checkTitle = "",
  accountNumber,
  isRemediable = false,
}) => {
  const dispatch = useDispatch();
  const { selectedProvider } = useContext(CloudProviderContext);
  const { isGettingCspmResources, getCspmResourcesError, getCspmResourcesMessage, cspmResources } = useSelector((state) => state.cspm);

  const activeRequestsKey =
    accountNumber && checkId ? `${accountNumber}:${checkId}` : null;
  const activeRequestsEntry = useSelector((state) =>
    activeRequestsKey ? state.remediation?.activeRequestsByKey?.[activeRequestsKey] : null
  );

  const activeRequestByArn = useMemo(() => {
    const map = new Map();
    const rows = activeRequestsEntry?.active_requests;
    if (!Array.isArray(rows)) return map;
    for (const item of rows) {
      if (item?.resource_arn && !map.has(item.resource_arn)) {
        map.set(item.resource_arn, item);
      }
    }
    return map;
  }, [activeRequestsEntry]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedService, setSelectedService] = useState('all');
  const [expandedRow, setExpandedRow] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [selectedArns, setSelectedArns] = useState(() => new Set());
  const [fixFormOpen, setFixFormOpen] = useState(false);
  const [remediationPreviewUrl, setRemediationPreviewUrl] = useState(null);

  const openRemediationPreview = useCallback((url) => {
    if (isHttpUrlString(url)) setRemediationPreviewUrl(String(url).trim());
    else if (url) window.open(String(url), "_blank", "noopener,noreferrer");
  }, []);

  const closeRemediationPreview = useCallback(() => {
    setRemediationPreviewUrl(null);
  }, []);

  useEffect(() => {
    if (!open) setRemediationPreviewUrl(null);
  }, [open]);

  useEffect(() => {
    if (open && checkId && runId) {
      setLoading(true);
      dispatch(clearAllCspmMessages());
      dispatch(getCspmResources({ checkId, runId }));
      setSelectedRegion('all');
      setSelectedService('all');
      setStatusFilter('FAILED');
      setSearch('');
      setExpandedRow(null);
      setSelectedArns(new Set());
    }
  }, [open, checkId, runId, dispatch]);

  useEffect(() => {
    if (open && accountNumber && checkId && isRemediable) {
      dispatch(fetchActiveRequests({ account_number: accountNumber, check_id: checkId }));
    }
  }, [open, accountNumber, checkId, isRemediable, dispatch]);

  useEffect(() => {
    const handleFilterEvent = (event) => {
      setStatusFilter(event.detail.filter);
    };
    window.addEventListener('setCspmFilter', handleFilterEvent);
    return () => window.removeEventListener('setCspmFilter', handleFilterEvent);
  }, []);

  useEffect(() => {
    if (!open) {
      setStatusFilter(null);
      setSelectedRegion('all');
      setSelectedService('all');
      setSearch('');
      setExpandedRow(null);
      setSelectedArns(new Set());
      setFixFormOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (getCspmResourcesMessage || getCspmResourcesError) {
      setLoading(false);
      dispatch(clearAllCspmMessages());
    }
  }, [getCspmResourcesMessage, getCspmResourcesError, dispatch]);

  const filteredResources = useMemo(() => {
    if (!cspmResources?.auditCheckList) return [];
    let resources = [...cspmResources.auditCheckList];
    if (selectedRegion !== 'all') {
      resources = resources.filter(r => r.region === selectedRegion);
    }
    if (selectedService !== 'all') {
      resources = resources.filter(r => r.Service === selectedService);
    }
    if (statusFilter) {
      resources = resources.filter(r => r.status === statusFilter);
    }
    if (search) {
      const searchLower = search.toLowerCase();
      resources = resources.filter(resource =>
        Object.values(resource).some(val =>
          val && String(val).toLowerCase().includes(searchLower)
        )
      );
    }
    const severityOrder = { CRITICAL: 1, HIGH: 2, MEDIUM: 3, LOW: 4, INFORMATIONAL: 5 };
    resources.sort((a, b) => {
      if ((a.status === 'FAILED') && (b.status !== 'FAILED')) return -1;
      if ((a.status !== 'FAILED') && (b.status === 'FAILED')) return 1;
      const sevA = severityOrder[(a.Severity || '').toUpperCase()] || 99;
      const sevB = severityOrder[(b.Severity || '').toUpperCase()] || 99;
      return sevA - sevB;
    });
    return resources;
  }, [cspmResources, search, selectedRegion, selectedService, statusFilter]);

  const failedFiltered = useMemo(
    () => filteredResources.filter((r) => r.status === 'FAILED' && r.resource_arn),
    [filteredResources]
  );

  const selectableFailed = useMemo(
    () => failedFiltered.filter((r) => !activeRequestByArn.has(r.resource_arn)),
    [failedFiltered, activeRequestByArn]
  );

  const hasFailedResources = useMemo(
    () =>
      (cspmResources?.failedResources ?? 0) > 0 ||
      (cspmResources?.auditCheckList?.some((r) => r.status === 'FAILED') ?? false),
    [cspmResources]
  );

  const showRequestFix =
    isRemediable && statusFilter !== 'PASSED' && hasFailedResources;

  useEffect(() => {
    if (!showRequestFix) {
      setSelectedArns(new Set());
      setFixFormOpen(false);
    }
  }, [showRequestFix]);

  useEffect(() => {
    if (activeRequestByArn.size === 0) return;
    setSelectedArns((prev) => {
      let changed = false;
      const next = new Set(prev);
      for (const arn of prev) {
        if (activeRequestByArn.has(arn)) {
          next.delete(arn);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [activeRequestByArn]);

  const toggleArn = (arn) => {
    setSelectedArns((prev) => {
      const next = new Set(prev);
      if (next.has(arn)) next.delete(arn);
      else next.add(arn);
      return next;
    });
  };

  const selectAllFailedVisible = () => {
    setSelectedArns(new Set(selectableFailed.map((r) => r.resource_arn)));
  };

  const clearSelection = () => setSelectedArns(new Set());

  const allFailedVisibleSelected =
    selectableFailed.length > 0 &&
    selectableFailed.every((r) => selectedArns.has(r.resource_arn));

  const refetchActiveRequests = () => {
    if (accountNumber && checkId) {
      dispatch(fetchActiveRequests({ account_number: accountNumber, check_id: checkId }));
    }
  };

  return (
  <>
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: 180, sm: 220, md: 600, lg: 900, xl: 1200 },
          maxWidth: '100vw',
          height: '100%',
          maxHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          p: 3,
          height: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          background: '#F9F9F9',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0 }}>
          <Typography variant="h6" color="#333">
            Resource list for the check — <b>{checkTitle || `Check ${checkId}`}</b>
          </Typography>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Box>
        {loading || isGettingCspmResources ? (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <CircularProgress color="primary" />
          </Box>
        ) : getCspmResourcesError ? (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <ErrorUI error={getCspmResourcesError} onRetry={() => {
              setLoading(true);
              dispatch(getCspmResources({ checkId, runId }));
            }} />
          </Box>
        ) : !cspmResources || !cspmResources.auditCheckList || cspmResources.auditCheckList.length === 0 ? (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Typography variant="h6" color="text.secondary">
              No resources data available
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Paper elevation={0} sx={{ p: 2, mb: 2, flexShrink: 0, backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', gap: 2 }}>
                <Chip label={`Total: ${cspmResources.totalResources || filteredResources.length}`} size="small" sx={{ backgroundColor: '#e9ecef', color: '#495057', fontWeight: 600 }} />
                {showRequestFix && (
                  <Chip
                    label={
                      activeRequestsEntry?.status === 'loading'
                        ? 'Active Fixes: …'
                        : `Active Fixes: ${
                            activeRequestsEntry?.status === 'succeeded'
                              ? activeRequestByArn.size
                              : 0
                          }`
                    }
                    size="small"
                    sx={{
                      backgroundColor: activeRequestByArn.size > 0 ? '#e3f2fd' : '#f5f5f5',
                      color: activeRequestByArn.size > 0 ? '#1565c0' : '#616161',
                      fontWeight: 600,
                    }}
                  />
                )}
                <Chip
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>Passed: {cspmResources.passedResources || 0}</span>
                      {statusFilter === 'PASSED' ? <RemoveIcon sx={{ fontSize: 16, ml: 0.5, color: '#2e7d32' }} /> : <AddIcon sx={{ fontSize: 16, ml: 0.5, color: '#2e7d32' }} />}
                    </Box>
                  }
                  size="small"
                  sx={{ backgroundColor: statusFilter === 'PASSED' ? '#c8e6c9' : '#e8f5e9', color: '#2e7d32', fontWeight: 600, cursor: 'pointer', border: statusFilter === 'PASSED' ? '2px solid #388e3c' : 'none', pl: 1, pr: 1 }}
                  onClick={() => setStatusFilter(statusFilter === 'PASSED' ? null : 'PASSED')}
                />
                <Chip
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span>Failed: {cspmResources.failedResources || 0}</span>
                      {statusFilter === 'FAILED' ? <RemoveIcon sx={{ fontSize: 16, ml: 0.5, color: '#d32f2f' }} /> : <AddIcon sx={{ fontSize: 16, ml: 0.5, color: '#d32f2f' }} />}
                    </Box>
                  }
                  size="small"
                  sx={{ backgroundColor: statusFilter === 'FAILED' ? '#ffcdd2' : '#ffebee', color: '#d32f2f', fontWeight: 600, cursor: 'pointer', border: statusFilter === 'FAILED' ? '2px solid #d32f2f' : 'none', pl: 1, pr: 1 }}
                  onClick={() => setStatusFilter(statusFilter === 'FAILED' ? null : 'FAILED')}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Region</InputLabel>
                  <Select value={selectedRegion} label="Region" onChange={e => setSelectedRegion(e.target.value)}>
                    <MenuItem value="all">All</MenuItem>
                    {availableRegions.map(region => <MenuItem key={region} value={region}>{region}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Service</InputLabel>
                  <Select value={selectedService} label="Service" onChange={e => setSelectedService(e.target.value)}>
                    <MenuItem value="all">All</MenuItem>
                    {availableTypes.map(service => <MenuItem key={service} value={service}>{service}</MenuItem>)}
                  </Select>
                </FormControl>
                <Box sx={{ flexGrow: 1 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', background: '#F1F1F1', borderRadius: 1, px: 1, height: 36 }}>
                  <SearchIcon sx={{ color: '#888', fontSize: 20, mr: 1 }} />
                  <InputBase placeholder="Search Resources" value={search} onChange={e => setSearch(e.target.value)} sx={{ width: 200, fontSize: 14 }} />
                </Box>
              </Stack>
            </Paper>

            {showRequestFix && (
              <Paper elevation={0} sx={{ p: 1.5, mb: 2, flexShrink: 0, border: '1px solid #c8e6c9', backgroundColor: '#f1f8e9' }}>
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {selectedArns.size > 0 ? `${selectedArns.size} failed resource(s) selected` : 'Select failed resources to request a fix'}
                  </Typography>
                  <Button size="small" onClick={selectAllFailedVisible} disabled={selectableFailed.length === 0}>
                    Select all failed
                  </Button>
                  {selectedArns.size > 0 && (
                    <Button size="small" onClick={clearSelection}>Clear</Button>
                  )}
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<BuildIcon />}
                    disabled={selectedArns.size === 0}
                    onClick={() => setFixFormOpen(true)}
                    sx={{ backgroundColor: '#2e7d32', '&:hover': { backgroundColor: '#1b5e20' } }}
                  >
                    Request fix ({selectedArns.size || 0})
                  </Button>
                </Stack>
              </Paper>
            )}

            <Paper
              elevation={0}
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                borderRadius: 2,
                boxShadow: '0px 4px 12px rgba(0,0,0,0.08)',
              }}
            >
              <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {showRequestFix && (
                      <TableCell padding="checkbox" sx={{ background: '#F5F5F5', width: 48 }}>
                        <Checkbox
                          size="small"
                          indeterminate={selectedArns.size > 0 && !allFailedVisibleSelected}
                          checked={allFailedVisibleSelected}
                          disabled={selectableFailed.length === 0}
                          onChange={() => (allFailedVisibleSelected ? clearSelection() : selectAllFailedVisible())}
                        />
                      </TableCell>
                    )}
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: 15, background: '#F5F5F5', color: '#333', width: 60, p: 0.5 }} />
                    <TableCell align="left" sx={{ fontWeight: 700, fontSize: 15, background: '#F5F5F5', color: '#333' }}>
                      {selectedProvider === "AZURE" ? "Azure Resource ID" : "Resource ARN"}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: 15, background: '#F5F5F5', color: '#333' }}>Region</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: 15, background: '#F5F5F5', color: '#333' }}>Service</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: 15, background: '#F5F5F5', color: '#333' }}>Status</TableCell>
                    {showRequestFix && (
                      <TableCell
                        align="center"
                        sx={{ fontWeight: 700, fontSize: 15, background: '#F5F5F5', color: '#333', width: 48, p: 0.5 }}
                      />
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredResources.map((resource, idx) => {
                    const isFailed = resource.status === 'FAILED';
                    const arn = resource.resource_arn;
                    const activeReq = arn ? activeRequestByArn.get(arn) : null;
                    return (
                      <React.Fragment key={arn || idx}>
                        <TableRow
                          hover
                          selected={expandedRow === idx}
                          onClick={() => setExpandedRow(expandedRow === idx ? null : idx)}
                          style={{ cursor: 'pointer' }}
                          sx={activeReq ? { bgcolor: 'rgba(25, 118, 210, 0.04)' } : undefined}
                        >
                          {showRequestFix && (
                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                size="small"
                                checked={Boolean(arn && selectedArns.has(arn))}
                                disabled={!isFailed || !arn || Boolean(activeReq)}
                                onChange={() => arn && toggleArn(arn)}
                              />
                            </TableCell>
                          )}
                          <TableCell align="center" sx={{ p: 0.5 }}>
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setExpandedRow(expandedRow === idx ? null : idx); }}>
                              {expandedRow === idx ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                            </IconButton>
                          </TableCell>
                          <TableCell align="left">
                            <Typography
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.875rem',
                                wordBreak: 'break-all',
                                lineHeight: 1.4,
                              }}
                            >
                              {resource.resource_arn}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">{resource.region}</TableCell>
                          <TableCell align="center">{resource.Service}</TableCell>
                          <TableCell align="center">
                            <Typography sx={{ color: resource.status === 'FAILED' ? '#d32f2f' : '#2e7d32', fontWeight: 600, fontSize: 14 }}>
                              {resource.status}
                            </Typography>
                          </TableCell>
                          {showRequestFix && (
                            <TableCell align="center" onClick={(e) => e.stopPropagation()} sx={{ p: 0.5 }}>
                              {activeReq ? (
                                <ActiveRemediationHint request={activeReq} />
                              ) : activeRequestsEntry?.status === 'loading' && isFailed ? (
                                <CircularProgress size={16} />
                              ) : null}
                            </TableCell>
                          )}
                        </TableRow>
                        {expandedRow === idx && (
                          <TableRow>
                            <TableCell colSpan={showRequestFix ? 7 : 5} sx={{ background: '#f8fafd', borderBottom: '1px solid #e3eafc', p: 2 }}>
                              <Box>
                                {activeReq && <ActiveRemediationDetailPanel request={activeReq} />}
                                <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                                  <b>Severity:</b>
                                  <span style={{ marginLeft: 8, color: getSeverityBadgeProps(resource.Severity).text, fontWeight: 700, fontSize: 13 }}>
                                    {resource.Severity}
                                  </span>
                                  &nbsp; <b>Type:</b> {resource.Type}
                                </Typography>
                                {resource.Remediation?.Text && (
                                  <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                                    <b>Remediation:</b> {resource.Remediation.Text}
                                    {resource.Remediation.Url && (
                                      <Typography
                                        component="button"
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          openRemediationPreview(resource.Remediation.Url);
                                        }}
                                        sx={{
                                          display: "inline",
                                          ml: 1,
                                          p: 0,
                                          border: "none",
                                          background: "none",
                                          color: "#1976d2",
                                          textDecoration: "underline",
                                          cursor: "pointer",
                                          font: "inherit",
                                        }}
                                      >
                                        Reference
                                      </Typography>
                                    )}
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
              </TableContainer>
              {showRequestFix && (
                <Box sx={{ flexShrink: 0 }}>
                  <RemediationStatusLegend />
                </Box>
              )}
            </Paper>
          </Box>
        )}
      </Box>
    </Drawer>

    <RemediationSnackbarProvider>
      <RequestFixForm
        open={fixFormOpen}
        onClose={() => setFixFormOpen(false)}
        preset={{
          checkId,
          checkTitle,
          accountNumber,
          resourceArns: Array.from(selectedArns),
        }}
        onSubmitted={() => {
          clearSelection();
          refetchActiveRequests();
        }}
      />
    </RemediationSnackbarProvider>
    <RemediationPreviewPanel
      url={remediationPreviewUrl}
      onClose={closeRemediationPreview}
    />
  </>
  );
};

export default CSPMResourcesDrawer;
